import React, { useEffect, useState, useCallback } from "react";
import {
    Tag, Typography, Button, Modal, Card, Divider,
    message, Avatar, List, Descriptions, Spin, Badge
} from "antd";
import { EyeOutlined, CloseCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import DataTable from "../component/ui/DataTable";
import OrdersServ from "../service/OrdersServ";
import Breadcrumbs from "../component/Breadcrumbs";
import { usePreference } from "../context/PreferenceContext";
import "../css/orders.css";
import EmptyState from "../util/EmptyState";
import { getImageUrl } from "../component/SafeImage";

const { Text } = Typography;

const STATUS_COLORS = {
    requested: "orange",
    approved: "blue",
    rejected: "red",
    refund_completed: "green",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} • ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
};

const BuyerCancellationRequests = () => {
    const { t, formatPrice } = usePreference();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    const fetchRequests = useCallback(async (showLoading = true) => {
        try {
            if (showLoading && requests.length === 0) setLoading(true);
            const cached = localStorage.getItem('cached_buyer_cancellations');
            if (cached && showLoading) {
                try {
                    const parsed = JSON.parse(cached);
                    setRequests(parsed);
                    setLoading(false); // Stop skeleton immediately
                } catch(e) {}
            }

            // Fetch both sources in parallel
            const [returnsRes, ordersRes] = await Promise.allSettled([
                OrdersServ.getMyReturns(),
                OrdersServ.getMyOrders(),
            ]);

            // 1. ReturnRequest records with type=cancel or completed returns
            let cancelRecords = [];
            if (returnsRes.status === "fulfilled") {
                const allReturns = returnsRes.value?.data || (Array.isArray(returnsRes.value) ? returnsRes.value : []);
                cancelRecords = allReturns.filter(
                    (r) => r.type === "cancel" || String(r.status).toLowerCase() === "refund_completed"
                );
            }

            // 2. Direct Orders that were cancelled before reaching ReturnRequest flow
            let directCancelOrders = [];
            if (ordersRes.status === "fulfilled") {
                const allOrders = ordersRes.value?.data || (Array.isArray(ordersRes.value) ? ordersRes.value : []);
                // Filter orders that are cancelled
                directCancelOrders = allOrders.filter(
                    (o) => String(o.status).toLowerCase() === "cancelled"
                );
            }

            // 3. Deduplicate
            const returnOrderIds = new Set(cancelRecords.map(r => r.order?._id?.toString() || r.order?.toString()));
            const uniqueDirectCancelOrders = directCancelOrders.filter(o => !returnOrderIds.has(o._id?.toString()));

            // Normalize formats for rendering consistency
            const normalizedDirectCancels = uniqueDirectCancelOrders.map(o => ({
                _id: o._id,
                returnId: "DIR_CANCEL_" + (o.orderId || o._id.substring(0, 5).toUpperCase()),
                order: o,
                user: o.user,
                shop: o.shopId || o.shop,
                items: o.items || [],
                status: "cancelled", // standard UI badge mapping
                refundAmount: o.walletUsedAmount || o.grandTotal || 0, // approximation if not captured directly
                type: "cancel",
                createdAt: o.updatedAt || o.createdAt, 
                isDirectOrder: true
            }));

            // 4. Combine and Sort
            const combined = [...cancelRecords, ...normalizedDirectCancels];
            const sorted = combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            localStorage.setItem('cached_buyer_cancellations', JSON.stringify(sorted));
            setRequests(sorted);
        } catch (error) {
            console.error("Fetch Error:", error);
            message.error("Failed to load cancellation requests");
        } finally {
            setLoading(false);
        }
    }, [requests.length]);

    useEffect(() => {
        fetchRequests(true);
    }, []);

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setIsModalOpen(true);
    };

    const StatusTag = ({ status }) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
            {status?.toUpperCase()?.replace(/_/g, " ")}
        </Tag>
    );

    const columns = [
        {
            title: t("product", "Product"),
            key: "product",
            render: (_, record) => {
                const firstItem = record.items?.[0];
                const moreCount = (record.items?.length || 1) - 1;
                return (
                    <div className="pcu-flex-center-y pcu-gap-10">
                        <Avatar
                            shape="square"
                            size={56}
                            src={getImageUrl(firstItem?.productImage || firstItem?.imageUrl || firstItem?.product?.imageUrl)}
                        />
                        <div>
                            <Text strong>{firstItem?.productName || firstItem?.name || firstItem?.product?.name || "Unknown Product"}</Text>
                            {moreCount > 0 && (
                                <div className="pcu-font-12 pcu-color-muted">+ {moreCount} more items</div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: t("order_id", "Order ID"),
            key: "orderId",
            render: (_, record) => (
                <Text strong>#{record.order?.orderId || record.returnId}</Text>
            ),
        },
        {
            title: t("phone", "Phone"),
            key: "phone",
            render: (_, record) => (
                <Text>{record.order?.deliveryAddress?.phone || record.order?.shippingAddress?.phone || record.order?.user?.phone || "—"}</Text>
            ),
        },
        {
            title: t("date", "Date"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => <Text className="pcu-font-12">{formatDate(date)}</Text>,
        },
        {
            title: t("status", "Status"),
            dataIndex: "status",
            key: "status",
            render: (status) => <StatusTag status={status} />,
        },
    ];

    const MobileCard = ({ req }) => {
        const firstItem = req.items?.[0];
        return (
            <Card
                size="small"
                className="pcu-radius-8 pcu-shadow-sm"
                style={{ cursor: "pointer", marginBottom: 10 }}
                onClick={() => handleViewDetails(req)}
            >
                <div className="pcu-flex pcu-justify-between pcu-items-start pcu-margin-bottom-12">
                    <div className="pcu-flex-center-y pcu-gap-10">
                        <Avatar shape="square" size={48} src={getImageUrl(firstItem?.productImage || firstItem?.imageUrl || firstItem?.product?.imageUrl)} />
                        <div>
                            <Text strong className="pcu-font-13">
                                {firstItem?.productName || firstItem?.name || firstItem?.product?.name || "Unknown Product"}
                            </Text>
                            <div>
                                <Text type="secondary" className="pcu-font-11">
                                    #{req.order?.orderId || req.returnId}
                                </Text>
                            </div>
                        </div>
                    </div>
                    <StatusTag status={req.status} />
                </div>
                <Divider className="pcu-margin-y-8" />
                <div className="pcu-flex pcu-justify-between">
                    <div>
                        <Text type="secondary" className="pcu-font-11 pcu-display-block">
                            {t("reason", "Reason")}
                        </Text>
                        <Text className="pcu-font-12">{req.reason || "—"}</Text>
                    </div>
                    <div className="pcu-text-right">
                        <Text type="secondary" className="pcu-font-11 pcu-display-block">
                            {t("phone", "Phone")}
                        </Text>
                        <Text className="pcu-font-12">{req.order?.deliveryAddress?.phone || req.order?.shippingAddress?.phone || req.order?.user?.phone || "—"}</Text>
                    </div>
                </div>
                <div className="pcu-margin-top-8">
                    <Text type="secondary" className="pcu-font-11">{formatDate(req.createdAt)}</Text>
                </div>
            </Card>
        );
    };

    return (
        <div className="sd-container pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <CloseCircleOutlined style={{ fontSize: '22px', color: '#dc2626' }} />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>
                            {t("cancellations_and_refunds", "Cancellations & Refunds")}
                        </h1>
                        <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>
                            {requests.length} {t("requests_found", "requests total")}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={fetchRequests} 
                        loading={loading}
                        style={{
                            height: '40px',
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            background: '#fff',
                            color: '#475569',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        {t("refresh", "Refresh")}
                    </Button>
                </div>
            </div>

            <div className="pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden" style={{ border: '1px solid #f1f5f9' }}>
                <div className="pcu-padding-24">
                {loading ? (
                    <div className="pcu-flex-center pcu-padding-y-100">
                        <Spin size="large" />
                    </div>
                ) : requests.length === 0 ? (
                    <EmptyState
                        title={t("no_cancellations", "No Cancellations or Refunds Yet")}
                        description={t(
                            "no_cancellations_desc",
                            "When you cancel an order or get a refund, it will appear here."
                        )}
                        gradient="orange"
                        showAction={false}
                        icon="inbox"
                    />
                ) : isMobile ? (
                    <div>
                        {requests.map((req) => (
                            <MobileCard key={req._id} req={req} />
                        ))}
                    </div>
                ) : (
                    <div className="pcu-border-top pcu-margin-x-n24 pcu-margin-top-16">
                        <DataTable
                            columns={columns}
                            data={requests}
                            rowKey="_id"
                            loading={loading}
                            pagination={{ 
                                position: ["bottomRight"], 
                                pageSize: 10,
                                onChange: (page) => {
                                    const pageContent = document.querySelector('.page-content');
                                    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                            }}
                            onRow={(record) => ({
                                onClick: () => handleViewDetails(record),
                                style: { cursor: "pointer" },
                            })}
                        />
                    </div>
                )}
            </div>

            {/* Details Modal */}
            <Modal
                title={
                    <span>
                        <CloseCircleOutlined style={{ color: "#dc2626", marginRight: 8 }} />
                        {t("cancellation_details", "Cancellation Details")}
                        {selectedRequest && ` — #${selectedRequest.order?.orderId || selectedRequest.returnId}`}
                    </span>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        {t("close", "Close")}
                    </Button>,
                ]}
                width={680}
            >
                {selectedRequest && (
                    <>
                        {/* Status Banner */}
                        <Card
                            size="small"
                            style={{
                                marginBottom: 20,
                                background:
                                    selectedRequest.status === "refund_completed"
                                        ? "rgba(34,197,94,0.06)"
                                        : selectedRequest.status === "rejected"
                                            ? "rgba(239,68,68,0.06)"
                                            : "rgba(255,129,16,0.06)",
                                borderColor:
                                    selectedRequest.status === "refund_completed"
                                        ? "#86efac"
                                        : selectedRequest.status === "rejected"
                                            ? "#fca5a5"
                                            : "#fed7aa",
                            }}
                        >
                            <div className="pcu-flex-center-y pcu-gap-10">
                                <CloseCircleOutlined
                                    style={{
                                        fontSize: 20,
                                        color:
                                            selectedRequest.status === "refund_completed"
                                                ? "#16a34a"
                                                : "#dc2626",
                                    }}
                                />
                                <div>
                                    <Text strong>
                                        {t("order_cancellation", "Order Cancellation")} —{" "}
                                        <StatusTag status={selectedRequest.status} />
                                    </Text>
                                    <div className="pcu-margin-top-4">
                                        <Text type="secondary" className="pcu-font-12">
                                            {t("reason", "Reason")}: {selectedRequest.reason}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <Descriptions bordered size="small" style={{ marginBottom: 20 }}>
                            <Descriptions.Item label={t("order_id", "Order ID")} span={2}>
                                #{selectedRequest.order?.orderId || "—"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("request_id", "Request ID")}>
                                {selectedRequest._fromOrder ? "—" : selectedRequest.returnId}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("refund_value", "Refund Amount")} span={2}>
                                <Text strong className="pcu-color-orange">
                                    {formatPrice(selectedRequest.refundAmount)}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t("date", "Date")} span={3}>
                                {formatDate(selectedRequest.createdAt)}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left">{t("cancelled_items", "Cancelled Items")}</Divider>
                        <List
                            itemLayout="horizontal"
                            dataSource={selectedRequest.items || []}
                            renderItem={(item) => (
                                <List.Item>
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                shape="square"
                                                size={56}
                                                src={getImageUrl(item.productImage || item.imageUrl || item.product?.imageUrl)}
                                            />
                                        }
                                        title={item.productName || item.name || item.product?.name || "Unknown Product"}
                                        description={`${t("quantity", "Qty")}: ${item.quantity || item.returnQty || 1}`}
                                    />
                                    <Text strong>{formatPrice((item.price || item.product?.price || 0) * (item.quantity || item.returnQty || 1))}</Text>
                                </List.Item>
                            )}
                        />
                    </>
                )}
            </Modal>
        </div>
        </div>
    );
};

export default BuyerCancellationRequests;
